import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getDistrictBySlug, getDistrictStats, fmtNum, fmtUSD } from '../../data/districts';
import DemoBanner from '../../components/demo/DemoBanner';
import DistrictHero from '../../components/demo/DistrictHero';
import DistrictAnalytics from '../../components/demo/DistrictAnalytics';
import DemoEventCard from '../../components/demo/DemoEventCard';
import SchoolCard from '../../components/demo/SchoolCard';
import DemoCheckout from '../../components/demo/DemoCheckout';
import { Container, SectionHeader, StatCard, Button } from '../../components/ui';

function DistrictDemo() {
  const { districtSlug } = useParams();
  const district = getDistrictBySlug(districtSlug);

  const [modalOpen, setModalOpen] = useState(false);
  const [activeEvent, setActiveEvent] = useState(null);

  if (!district) {
    return (
      <div className="min-h-screen bg-ink-50 flex flex-col">
        <DemoBanner />
        <div className="flex-grow flex flex-col items-center justify-center text-center px-6">
          <h1 className="text-2xl font-bold font-display text-ink-900 mb-2">District not found</h1>
          <p className="text-ink-500 mb-6">We don't have a demo for "{districtSlug}" yet.</p>
          <Button to="/demo">View all districts</Button>
        </div>
      </div>
    );
  }

  const stats = getDistrictStats(district);
  const { primary, secondary, accent } = district.colors;

  // Sample "all-access" pass used by the generic CTA so it also runs the flow.
  const samplePass = {
    title: `${district.shortName} All-Access Pass`,
    school: district.name,
    price: '$25',
    venue: 'Any district event',
  };

  const handleBuy = (event) => {
    setActiveEvent(event || samplePass);
    setModalOpen(true);
  };

  const kpis = [
    { label: 'Tickets sold today', value: fmtNum(stats.ticketsToday), icon: '🎟️', trend: { dir: 'up', value: '12%' }, hint: 'vs yesterday' },
    { label: 'Revenue today', value: fmtUSD(stats.revenueToday), icon: '💵', trend: { dir: 'up', value: '8%' }, hint: 'vs yesterday' },
    { label: 'Active schools', value: `${stats.activeSchools}/${stats.schoolCount}`, icon: '🏫', hint: 'with live events' },
    { label: 'Events this week', value: fmtNum(stats.eventsThisWeek), icon: '📅', hint: 'across the district' },
  ];

  return (
    <div className="min-h-screen bg-ink-50">
      <DemoBanner />
      <DistrictHero district={district} stats={stats} />

      <Container className="py-10 sm:py-12 space-y-14">
        {/* Live snapshot KPIs */}
        <section>
          <SectionHeader
            eyebrow="Live snapshot"
            title="District at a glance"
            subtitle="What administrators see the moment they log in."
            accent={primary}
          />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {kpis.map((k) => (
              <StatCard key={k.label} {...k} accent={accent} />
            ))}
          </div>
          <div className="mt-6">
            <DistrictAnalytics district={district} stats={stats} />
          </div>
        </section>

        {/* Athletics */}
        <section>
          <SectionHeader eyebrow="Athletics" title="🏈 Athletic Events" accent={primary} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {district.athleticEvents?.map((ev, i) => (
              <DemoEventCard key={i} event={ev} district={district} onBuy={handleBuy} />
            ))}
          </div>
        </section>

        {/* Fine Arts */}
        <section>
          <SectionHeader eyebrow="Fine Arts" title="🎭 Fine Arts Events" accent={secondary} />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {district.fineArtsEvents?.map((ev, i) => (
              <DemoEventCard key={i} event={ev} district={district} onBuy={handleBuy} />
            ))}
          </div>
        </section>

        {/* Schools */}
        <section>
          <SectionHeader eyebrow="Campuses" title="🏫 Schools" accent={accent} />

          {district.highSchools?.length > 0 && (
            <div className="mb-8">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-500 mb-3">
                High Schools
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {district.highSchools.map((s, i) => (
                  <SchoolCard key={i} school={s} district={district} />
                ))}
              </div>
            </div>
          )}

          {district.middleSchools?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-500 mb-3">
                Middle Schools
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {district.middleSchools.map((s, i) => (
                  <SchoolCard key={i} school={s} district={district} />
                ))}
              </div>
            </div>
          )}
        </section>

        {/* CTA */}
        <section
          className="relative overflow-hidden rounded-3xl text-white text-center px-6 py-12 sm:py-14"
          style={{ background: `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)` }}
        >
          <div
            className="pointer-events-none absolute -bottom-16 -left-16 w-72 h-72 rounded-full opacity-25 blur-3xl"
            style={{ backgroundColor: accent }}
          />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl font-extrabold font-display">
              Imagine this for {district.shortName}
            </h2>
            <p className="mt-2 opacity-90 max-w-2xl mx-auto">
              Branded ticketing, instant mobile QR tickets, gate check-in, and
              real-time sales reporting - all in one platform for {district.name}.
            </p>
            <button
              onClick={() => handleBuy(null)}
              className="mt-6 inline-flex items-center font-semibold rounded-xl px-6 py-3 transition-transform hover:scale-[1.03]"
              style={{ backgroundColor: accent, color: primary }}
            >
              See how it works →
            </button>
          </div>
        </section>
      </Container>

      <DemoCheckout
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        event={activeEvent}
        district={district}
      />

      <footer className="border-t border-ink-200 bg-white">
        <Container className="py-8 text-center text-sm text-ink-400">
          <Link to="/demo" className="text-brand-600 font-medium hover:underline">← Back to all districts</Link>
          <p className="mt-1">Powered by Valley Tickets</p>
        </Container>
      </footer>
    </div>
  );
}

export default DistrictDemo;
